import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface AnalyticsData {
  applicationTrends: Array<{
    month: string;
    applications: number;
    quotes: number;
  }>;
  departmentBreakdown: Array<{
    department: string;
    count: number;
  }>;
  quoteConversionRate: number;
  memberGrowth: Array<{
    month: string;
    members: number;
  }>;
  topPerformers: Array<{
    name: string;
    applications: number;
    quotes: number;
  }>;
}

export const useOrganizationAnalytics = (providerId: string) => {
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    applicationTrends: [],
    departmentBreakdown: [],
    quoteConversionRate: 0,
    memberGrowth: [],
    topPerformers: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!providerId) return;

      try {
        setIsLoading(true);
        setError(null);

        // Fetch applications and quotes data
        const { data: applications, error: applicationsError } = await supabase
          .from('applications')
          .select('id, created_at, created_by_peo_id')
          .eq('created_by_peo_id', providerId);

        if (applicationsError) throw applicationsError;

        const { data: quotes, error: quotesError } = await supabase
          .from('peo_quotes')
          .select('id, created_at, status, created_by')
          .eq('provider_id', providerId);

        if (quotesError) throw quotesError;

        // Fetch members with profiles
        const { data: members, error: membersError } = await supabase
          .from('peo_representatives')
          .select(`
            id, 
            department, 
            created_at,
            user_id,
            profiles!inner(first_name, last_name)
          `)
          .eq('provider_id', providerId)
          .eq('verification_status', 'verified');

        if (membersError) throw membersError;

        // Generate last 6 months data
        const months = [];
        for (let i = 5; i >= 0; i--) {
          const date = new Date();
          date.setMonth(date.getMonth() - i);
          months.push({
            date,
            key: date.toISOString().slice(0, 7), // YYYY-MM
            label: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
          });
        }

        // Calculate application trends
        const applicationTrends = months.map(month => {
          const monthApplications = applications?.filter(app => 
            app.created_at && app.created_at.startsWith(month.key)
          ).length || 0;

          const monthQuotes = quotes?.filter(quote => 
            quote.created_at && quote.created_at.startsWith(month.key)
          ).length || 0;

          return {
            month: month.label,
            applications: monthApplications,
            quotes: monthQuotes
          };
        });

        // Calculate department breakdown
        const departmentCounts = new Map<string, number>();
        members?.forEach(member => {
          if (member.department) {
            departmentCounts.set(
              member.department, 
              (departmentCounts.get(member.department) || 0) + 1
            );
          }
        });

        const departmentBreakdown = Array.from(departmentCounts.entries()).map(([department, count]) => ({
          department,
          count
        }));

        // Calculate quote conversion rate
        const totalQuotes = quotes?.length || 0;
        const acceptedQuotes = quotes?.filter(q => q.status === 'accepted').length || 0;
        const quoteConversionRate = totalQuotes > 0 ? (acceptedQuotes / totalQuotes) * 100 : 0;

        // Calculate member growth
        const memberGrowth = months.map(month => {
          const membersUpToMonth = members?.filter(member => 
            member.created_at && member.created_at <= month.date.toISOString()
          ).length || 0;

          return {
            month: month.label,
            members: membersUpToMonth
          };
        });

        // Calculate top performers (by applications and quotes created)
        const performerStats = new Map();
        
        applications?.forEach(app => {
          if (app.created_by_peo_id) {
            const current = performerStats.get(app.created_by_peo_id) || { applications: 0, quotes: 0 };
            performerStats.set(app.created_by_peo_id, { 
              ...current, 
              applications: current.applications + 1 
            });
          }
        });

        quotes?.forEach(quote => {
          if (quote.created_by) {
            const current = performerStats.get(quote.created_by) || { applications: 0, quotes: 0 };
            performerStats.set(quote.created_by, { 
              ...current, 
              quotes: current.quotes + 1 
            });
          }
        });

        // Get top performers with names
        const topPerformers = Array.from(performerStats.entries())
          .map(([userId, stats]) => {
            const member = members?.find(m => m.user_id === userId);
            const name = member?.profiles 
              ? `${member.profiles.first_name || ''} ${member.profiles.last_name || ''}`.trim()
              : 'Unknown User';
            
            return {
              name: name || 'Unknown User',
              applications: stats.applications,
              quotes: stats.quotes
            };
          })
          .sort((a, b) => (b.applications + b.quotes) - (a.applications + a.quotes))
          .slice(0, 5);

        setAnalytics({
          applicationTrends,
          departmentBreakdown,
          quoteConversionRate: Math.round(quoteConversionRate * 100) / 100,
          memberGrowth,
          topPerformers
        });

      } catch (err) {
        console.error('Error fetching organization analytics:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch analytics');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, [providerId]);

  return { analytics, isLoading, error };
};